// Quick Booking Enquiry: default option lists, tabs, and upgrade of older saved configs
const split = (s) => s.split("|");

export const FIELD_TYPES = {
  search: "Searchable dropdown", select: "Dropdown", date: "Date picker", travellers: "Passenger picker",
  triptype: "Trip type (One-way / Return)", text: "Short text", textarea: "Long text",
};

export const DEFAULT_LISTS = [
  { key: "cities", label: "Cities & Airports", options: split(
    "Dubai (DXB)|Sharjah (SHJ)|Abu Dhabi (AUH)|Ras Al Khaimah (RKT)|Al Ain (AAN)|Muscat (MCT)|Doha (DOH)|Bahrain (BAH)|Kuwait (KWI)|Riyadh (RUH)|Jeddah (JED)|Dammam (DMM)|" +
    "Kochi (COK)|Thiruvananthapuram (TRV)|Kozhikode (CCJ)|Kannur (CNN)|Mangaluru (IXE)|Bengaluru (BLR)|Chennai (MAA)|Hyderabad (HYD)|Mumbai (BOM)|Delhi (DEL)|Goa (GOI)|Kolkata (CCU)|Ahmedabad (AMD)|Pune (PNQ)|Jaipur (JAI)|Coimbatore (CJB)|" +
    "London (LHR)|Paris (CDG)|Istanbul (IST)|Baku (GYD)|Tbilisi (TBS)|Almaty (ALA)|Cairo (CAI)|Nairobi (NBO)|Male (MLE)|Colombo (CMB)|Kathmandu (KTM)|Bangkok (BKK)|Phuket (HKT)|Kuala Lumpur (KUL)|Singapore (SIN)|Bali (DPS)|Hong Kong (HKG)|Tokyo (NRT)|New York (JFK)|Toronto (YYZ)|Sydney (SYD)") },
  { key: "countries", label: "Countries", options: split(
    "United Arab Emirates|India|Saudi Arabia|Oman|Qatar|Bahrain|Kuwait|United Kingdom|United States|Canada|Australia|New Zealand|Schengen (Europe)|France|Germany|Italy|Spain|Switzerland|Netherlands|Greece|Turkey|Azerbaijan|Georgia|Armenia|Kazakhstan|Uzbekistan|Russia|Egypt|Kenya|Tanzania|South Africa|Mauritius|Seychelles|Maldives|Sri Lanka|Nepal|Bhutan|Thailand|Malaysia|Singapore|Indonesia|Vietnam|Cambodia|Philippines|China|Hong Kong|Japan|South Korea") },
  { key: "nationalities", label: "Nationalities", options: split(
    "Indian|Emirati|Pakistani|Bangladeshi|Sri Lankan|Nepali|Filipino|Egyptian|Jordanian|Lebanese|Syrian|Saudi|Omani|Qatari|Bahraini|Kuwaiti|British|American|Canadian|Australian|Irish|French|German|Italian|Spanish|Russian|Ukrainian|Kazakh|Chinese|Indonesian|Malaysian|Thai|Vietnamese|Nigerian|Kenyan|Ethiopian|South African|Sudanese|Iranian|Afghan") },
  { key: "cabin", label: "Cabin class", options: ["Economy", "Premium Economy", "Business", "First"] },
  { key: "visa_types", label: "Visa types", options: ["Tourist", "Business", "Transit", "Visit (Family)", "Student"] },
];

const F = (label, type, extra = {}) => ({ label, type, placeholder: "", required: false, ...extra });

export const DEFAULT_TABS = [
  { key: "flights", label: "Flights", icon: "plane", fields: [
    F("Trip Type", "triptype"), F("From", "search", { source: "cities", placeholder: "Departure city", required: true }),
    F("To", "search", { source: "cities", placeholder: "Destination city", required: true }),
    F("Travel Date", "date", { required: true }), F("Return Date", "date", { returnOnly: true }),
    F("Passengers", "travellers"), F("Cabin Class", "select", { source: "cabin" })] },
  { key: "visa", label: "Visa Services", icon: "visa", fields: [
    F("Country", "search", { source: "countries", placeholder: "Destination country", required: true }),
    F("Nationality", "search", { source: "nationalities", placeholder: "Your nationality", required: true }),
    F("Visa Type", "select", { source: "visa_types" }), F("Travel Date", "date"), F("Travellers", "travellers")] },
  { key: "hotels", label: "Hotels", icon: "hotel", fields: [
    F("Destination", "search", { source: "cities", placeholder: "City or hotel", required: true }),
    F("Check-in", "date", { required: true }), F("Check-out", "date"), F("Guests", "travellers")] },
];

export const DEFAULT_BUDGETS = [
  { label: "Under AED 1,000", min: 0, max: 1000 },
  { label: "AED 1,000 – 2,500", min: 1000, max: 2500 },
  { label: "AED 2,500 – 5,000", min: 2500, max: 5000 },
  { label: "AED 5,000 – 10,000", min: 5000, max: 10000 },
  { label: "AED 10,000+", min: 10000, max: null },
];
const TAB_SERVICE = { flights: "Flights", visa: "Visa", hotels: "Hotels" };

const fmt = (n) => Math.round(n).toLocaleString();
// Estimated points for a budget range using the live reward rule (mirrors backend earn maths)
export function estimatePoints(rules, service, budget) {
  if (!rules || !budget) return null;
  const rule = rules.service_rules?.[service] || rules.default_rule;
  if (!rule) return null;
  const pv = rules.point_value_aed > 0 ? rules.point_value_aed : 1;
  const cap = rules.max_points_per_transaction || Infinity;
  const pts = (amt) => {
    if (amt <= 0 || amt < (rules.min_invoice_amount || 0)) return 0;
    const aed = rule.type === "percentage" ? amt * rule.value / 100 : rule.value;
    return Math.min(cap, Math.round(aed / pv));
  };
  const lo = pts(budget.min || 0), hi = budget.max ? pts(budget.max) : null;
  const text = hi === null ? (lo >= cap ? fmt(cap) : `${fmt(Math.max(lo, 0))}+`) : lo === hi ? fmt(hi) : `${fmt(lo)}–${fmt(hi)}`;
  return { text, aed: hi === null ? (lo >= cap ? fmt(cap * pv) : `${fmt(lo * pv)}+`) : lo === hi ? fmt(hi * pv) : `${fmt(lo * pv)}–${fmt(hi * pv)}`, zero: hi === 0 };
}

export const ruleText = (rules, service) => {
  const r = rules && (rules.service_rules?.[service] || rules.default_rule);
  if (!r) return null;
  return r.type === "percentage" ? `${r.value}% back in points` : `${r.value} AED in points per booking`;
};

export const DEFAULT_ROUTES = [
  { label: "Dubai → Kochi", tab: "flights", from: "Dubai (DXB)", to: "Kochi (COK)" },
  { label: "Sharjah → Thiruvananthapuram", tab: "flights", from: "Sharjah (SHJ)", to: "Thiruvananthapuram (TRV)" },
  { label: "Dubai → Baku", tab: "flights", from: "Dubai (DXB)", to: "Baku (GYD)" },
];

const AUTO = { from: "cities", to: "cities", destination: "cities", country: "countries", nationality: "nationalities" };
const key = (l) => (l || "").toLowerCase().replace(/[^a-z]/g, "");
const defaultField = (tabKey, label) => DEFAULT_TABS.find((t) => t.key === tabKey)?.fields.find((f) => key(f.label) === key(label));

// Older saved configs used free-text fields; upgrade them in memory without touching stored data
export function normalizeQuick(c) {
  const lists = c.lists?.length ? c.lists : DEFAULT_LISTS;
  const extras = { budgets: c.budgets?.length ? c.budgets : DEFAULT_BUDGETS };
  const withService = (tabs) => tabs.map((t) => (t.service !== undefined ? t : { ...t, service: TAB_SERVICE[t.key] || "" }));
  if (c.v2) return { ...c, ...extras, tabs: withService(c.tabs || []), lists, routes: c.routes || [], contact: c.contact || { enabled: true } };
  const tabs = (c.tabs || []).map((t) => {
    let fields = (t.fields || []).map((f) => {
      const d = defaultField(t.key, f.label);
      const up = f.type === "text" && AUTO[key(f.label)] ? { type: "search", source: AUTO[key(f.label)] } : {};
      return { ...f, ...up, required: f.required ?? !!d?.required };
    });
    if (t.key === "visa" && !fields.some((f) => key(f.label) === "visatype")) {
      fields.splice(Math.min(2, fields.length), 0, F("Visa Type", "select", { source: "visa_types" }));
    }
    if (t.key === "flights" && !fields.some((f) => f.type === "triptype")) {
      const di = fields.findIndex((f) => f.type === "date");
      fields = [F("Trip Type", "triptype"), ...fields];
      if (di >= 0) fields.splice(di + 2, 0, F("Return Date", "date", { returnOnly: true }));
      fields.push(F("Cabin Class", "select", { source: "cabin" }));
    }
    return { ...t, fields };
  });
  return { ...c, ...extras, tabs: withService(tabs), lists, routes: c.routes || DEFAULT_ROUTES, contact: c.contact || { enabled: true } };
}

export const fieldOptions = (f, lists) => (f.source ? lists.find((l) => l.key === f.source)?.options || [] : f.options || []);
